// Shared helpers for the Meeting module: timezone-correct conversion + display.
//
// Backend stores start_at/end_at as NAIVE UTC and a separate IANA `timezone`
// string. We render wall-clock times in the meeting's timezone and convert the
// scheduler's local input back to a UTC ISO string — all with the stdlib Intl
// API (no date-fns-tz dependency needed).

export const browserTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
};

// A small, practical IANA timezone list for the picker (India-first).
export const COMMON_TIMEZONES = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'Australia/Sydney', 'UTC',
];

// Parse a naive-UTC backend datetime ("2026-06-28T10:00:00") as a real instant.
export const parseUTC = (s) => {
  if (!s) return null;
  if (s instanceof Date) return s;
  // Append Z when the backend omitted an offset so it's read as UTC, not local.
  const hasTz = /[zZ]|[+-]\d\d:?\d\d$/.test(s);
  return new Date(hasTz ? s : `${s}Z`);
};

// Offset (ms) of `timeZone` at the given UTC instant.
const tzOffsetMs = (utcMs, timeZone) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const m = {};
  for (const p of dtf.formatToParts(new Date(utcMs))) m[p.type] = p.value;
  const asUTC = Date.UTC(+m.year, +m.month - 1, +m.day, +m.hour, +m.minute, +m.second);
  return asUTC - utcMs;
};

// "2026-06-28T15:30" entered as wall-clock in `timeZone` → UTC ISO string.
export const wallClockToISO = (localStr, timeZone) => {
  if (!localStr) return null;
  const [datePart, timePart] = localStr.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi);
  const offset = tzOffsetMs(utcGuess, timeZone);
  return new Date(utcGuess - offset).toISOString();
};

// A UTC instant → "YYYY-MM-DDTHH:mm" wall-clock string in `timeZone`, for the
// datetime-local input when editing an existing meeting.
export const isoToWallClock = (utcDate, timeZone) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  const m = {};
  for (const p of dtf.formatToParts(utcDate)) m[p.type] = p.value;
  return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
};

export const fmtInTz = (utcDate, timeZone, opts) =>
  new Intl.DateTimeFormat('en-GB', { timeZone, ...opts }).format(utcDate);

// Human "28 Jun 2026, 15:30 (IST-style label)" for a meeting.
export const fmtWhen = (m) => {
  const d = parseUTC(m.start_at);
  if (!d) return '';
  const date = fmtInTz(d, m.timezone, { day: '2-digit', month: 'short', year: 'numeric' });
  const time = fmtInTz(d, m.timezone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${date}, ${time}`;
};

export const STATUS_STYLES = {
  scheduled: { label: 'Scheduled', cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  ongoing: { label: 'Ongoing', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  completed: { label: 'Completed', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  cancelled: { label: 'Cancelled', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
};

// Calendar-event accent colour per status.
export const STATUS_DOT = {
  scheduled: 'bg-sky-500',
  ongoing: 'bg-emerald-500',
  completed: 'bg-slate-400',
  cancelled: 'bg-rose-500',
};

export const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

export const RECORD_CONTEXTS = ['project', 'task', 'campaign'];

// Time-block types: label + calendar colours (distinct from meetings).
export const BLOCK_TYPES = [
  { v: 'busy', label: 'Busy' },
  { v: 'focus', label: 'Focus Time' },
  { v: 'leave', label: 'On Leave' },
  { v: 'meeting', label: 'In a Meeting' },
  { v: 'ooo', label: 'Out of Office' },
  { v: 'custom', label: 'Custom' },
];
export const BLOCK_LABEL = Object.fromEntries(BLOCK_TYPES.map((b) => [b.v, b.label]));
export const BLOCK_DOT = {
  busy: 'bg-rose-400', focus: 'bg-indigo-400', leave: 'bg-purple-400',
  meeting: 'bg-amber-400', ooo: 'bg-purple-400', custom: 'bg-slate-400',
};
// Blocks render with a hatched/striped background so they read as "blocked",
// visually distinct from solid meeting chips.
export const BLOCK_BG = {
  busy: 'bg-rose-50 text-rose-700', focus: 'bg-indigo-50 text-indigo-700',
  leave: 'bg-purple-50 text-purple-700', meeting: 'bg-amber-50 text-amber-700',
  ooo: 'bg-purple-50 text-purple-700', custom: 'bg-slate-100 text-slate-700',
};
